import { BlockEmbed } from 'quill/blots/block';
import type { Range } from 'quill/core/selection';
import type { Context } from 'quill/modules/keyboard';
import findQuillByBlot from './findQuillByBlot';

export const isRangeACaret = (range: Range, line: Context['line']) =>
  line instanceof BlockEmbed ? range.length <= 1 : range.length === 0;

const convertToIntOrZero = (value: string | null) => {
  if (value) {
    const int = parseInt(value, 10);
    if (!Number.isNaN(int)) return int;
  }
  return 0;
};

export const isRangeAtLineVerticalEdge = (
  range: Range,
  line: Context['line'],
  side: 'top' | 'bottom',
) => {
  const quill = findQuillByBlot(line);
  if (!quill) return false;

  if (line instanceof BlockEmbed) {
    return true;
  }

  const selectionBounds = quill.selection.getBounds(range.index);
  if (!selectionBounds) return false;

  const lineRect = line.domNode.getBoundingClientRect();

  const styles = getComputedStyle(line.domNode);
  const padding = convertToIntOrZero(
    styles[side === 'top' ? 'paddingTop' : 'paddingBottom'],
  );
  const borderWidth = convertToIntOrZero(
    styles[side === 'top' ? 'borderTopWidth' : 'borderBottomWidth'],
  );
  const spacing = padding + borderWidth;

  const distance =
    side === 'top'
      ? selectionBounds.top - (lineRect.top + spacing)
      : lineRect.bottom - spacing - selectionBounds.bottom;

  return distance < convertToIntOrZero(styles.lineHeight);
};

export const getSiblingLine = (
  line: Context['line'],
  relation: 'previous' | 'next',
) => {
  const quill = findQuillByBlot(line);
  if (!quill) return null;
  const lineOffset = line.offset(line.scroll);
  const siblingLineIndex =
    relation === 'previous' ? lineOffset - 1 : lineOffset + line.length();

  if (relation === 'previous' && siblingLineIndex < 0) {
    return null;
  }
  if (relation === 'next' && siblingLineIndex > quill.getLength() - 1) {
    return null;
  }
  const [sibling] = quill.getLine(siblingLineIndex);
  return sibling;
};
